import React, { useState } from "react";
import { useIntl } from "react-intl";
import { ParagraphOutputLLMAnswer, LLMReference, LLMSuggestion } from "@/model";
import { toSpliced, withArray } from "@/util/arrayUtil";
import { useScenarioAutocompletes } from "@/hook";
import Button from "@/component/Button";
import { Textarea, TextInput } from "@/component/input";
import { SortableItemWrapper, SortableWrapper, SortableItem, toSortableItem } from "@/component/SortableWrapper";
import { AddIcon, DeleteIcon } from "@/assets/svgs/icons";

import { ParagraphInputsProps } from "../model";


const editablePayloadText = (payload: string) => {
    if (!payload || !payload.startsWith("text:")) {
        return "";
    }
    return payload.replace("text:", "");
};

const ParagraphOutputLLMAnswerCardInputs: React.FC<ParagraphInputsProps<ParagraphOutputLLMAnswer>> = (props) => {
    const { paragraph, onChange } = props;

    const intl = useIntl();
    const { variableAutocomplete } = useScenarioAutocompletes();

    const [sortableReferences, setSortableReferences] = useState<SortableItem<LLMReference>[]>(paragraph.data?.references?.map(toSortableItem) ?? []);
    const [sortableSuggestions, setSortableSuggestions] = useState<SortableItem<LLMSuggestion>[]>(paragraph.data?.suggestions?.map(toSortableItem) ?? []);

    const updateReferences = (references: SortableItem<LLMReference>[]) => {
        setSortableReferences(references);
        onChange({
            ...paragraph,
            data: {
                ...paragraph.data,
                references: references.map(item => item.data)
            }
        });
    };

    const updateSuggestions = (suggestions: SortableItem<LLMSuggestion>[]) => {
        setSortableSuggestions(suggestions);
        onChange({
            ...paragraph,
            data: {
                ...paragraph.data,
                suggestions: suggestions.map(item => item.data)
            }
        });
    };

    const onChangeReference = (index: number, field: keyof LLMReference, value: string) => {
        const newReference = { ...sortableReferences[index], data: { ...sortableReferences[index].data, [field]: value } };
        updateReferences(withArray(sortableReferences, index, newReference));
    };

    const onChangeSuggestion = (index: number, field: keyof LLMSuggestion, value: string) => {
        const newSuggestion = { ...sortableSuggestions[index], data: { ...sortableSuggestions[index].data, [field]: value } };
        updateSuggestions(withArray(sortableSuggestions, index, newSuggestion));
    };

    return (<>
        <div className="col-span-2 flex flex-col gap-1.5">
            <div className="h-6 flex gap-1 items-center">
                <div className="field-label">
                    {intl.formatMessage({ id: "b0039" })}
                </div>
            </div>
            <div className="flex flex-col gap-1">
                <div className="flex items-center gap-0.5">
                    <Textarea size="sm"
                              rows={1}
                              required
                              autocompletes={variableAutocomplete}
                              defaultValue={paragraph.data?.text ?? ""}
                              placeholder={intl.formatMessage({ id: "i04040" })}
                              onChange={value => onChange({
                                  ...paragraph,
                                  data: {
                                      ...paragraph.data,
                                      text: value
                                  }
                              })}
                    />
                </div>
            </div>
        </div>

        <div data-testid="paragraph-llm-refs"
             className="col-span-2 flex flex-col gap-1.5"
        >
            <div className="flex items-center gap-1">
                <div className="field-label">
                    {intl.formatMessage({ id: "i04079" })}
                </div>
                <Button variant="text-transparent"
                        aria-label={intl.formatMessage({ id: "b0009" })}
                        onClick={() => updateReferences([...sortableReferences, toSortableItem({ label: "" })])}
                >
                    <AddIcon className="size-3" />
                </Button>
            </div>
            <SortableWrapper items={sortableReferences}
                             className="flex flex-col gap-2"
                             onDragSortEnd={updateReferences}
            >{sortableReferences.map((reference, index) => (
                <SortableItemWrapper key={reference.id}
                                     item={reference}
                                     aria-label={`${intl.formatMessage({ id: "i04079" })} ${index + 1}`}
                >{dragHandle => (
                    <div className="w-full flex items-start gap-1">
                        <div className="label-input-grid-label">
                            {dragHandle}
                        </div>
                        <div className="w-full flex flex-col gap-1">
                            <TextInput required
                                       value={reference.data.label ?? ""}
                                       placeholder={intl.formatMessage({ id: "i04084" })}
                                       onChange={e => onChangeReference(index, "label", e.target.value)}
                            />
                            <input type="text"
                                   className="input-default"
                                   value={reference.data.link_mobile ?? ""}
                                   placeholder={intl.formatMessage({ id: "i04019" })}
                                   aria-label={intl.formatMessage({ id: "i04019_1" })}
                                   onChange={e => onChangeReference(index, "link_mobile", e.target.value)}
                            />
                            <input type="text"
                                   className="input-default"
                                   value={reference.data.link_pc ?? ""}
                                   placeholder={intl.formatMessage({ id: "i04020" })}
                                   aria-label={intl.formatMessage({ id: "i04020_1" })}
                                   onChange={e => onChangeReference(index, "link_pc", e.target.value)}
                            />
                            <input type="text"
                                className="input-default"
                                value={reference.data.information ?? ""}
                                placeholder={intl.formatMessage({ id: "i04021" })}
                                aria-label={intl.formatMessage({ id: "i04093" })}
                                onChange={e => onChangeReference(index, "information", e.target.value)}
                            />
                        </div>
                        <Button variant="text-transparent"
                                className="label-input-grid-label ml-1.5 mr-2 flex-none disabled:opacity-0 disabled:cursor-default"
                                aria-label={intl.formatMessage({ id: "b0006" })}
                                onClick={() => updateReferences(toSpliced(sortableReferences, index, 1))}
                        >
                            <DeleteIcon className="text-point-red size-4" />
                        </Button>
                    </div>
                )}</SortableItemWrapper>
            ))}</SortableWrapper>
        </div>

        <div data-testid="paragraph-llm-sugs"
             className="col-span-2 flex flex-col gap-1.5"
        >
            <div className="flex items-center gap-1">
                <div className="field-label">
                    {intl.formatMessage({ id: "i04080" })}
                </div>
                <Button variant="text-transparent"
                        aria-label={intl.formatMessage({ id: "b0009" })}
                        onClick={() => updateSuggestions([...sortableSuggestions, toSortableItem({ label: "" })])}
                >
                    <AddIcon className="size-3" />
                </Button>
            </div>
            <SortableWrapper items={sortableSuggestions}
                             className="flex flex-col gap-2"
                             onDragSortEnd={updateSuggestions}
            >{sortableSuggestions.map((suggestion, index) => (
                <SortableItemWrapper key={suggestion.id}
                                     item={suggestion}
                                     aria-label={`${intl.formatMessage({ id: "i04080" })} ${index + 1}`}
                >{dragHandle => (
                    <div className="w-full flex items-start gap-1">
                        <div className="label-input-grid-label">
                            {dragHandle}
                        </div>
                        <TextInput required
                                   value={suggestion.data.label ?? ""}
                                   placeholder={intl.formatMessage({ id: "i04070" })}
                                   onChange={e => onChangeSuggestion(index, "label", e.target.value)}
                        />
                        <input type="text"
                               className="input-default"
                               value={editablePayloadText(suggestion.data.payload ?? "")}
                               placeholder={intl.formatMessage({ id: "i04013" })}
                               aria-label={intl.formatMessage({ id: "i04013_1" })}
                               onChange={e => onChangeSuggestion(index, "payload", `text:${e.target.value}`)}
                        />
                        <Button variant="text-transparent"
                                className="label-input-grid-label ml-1.5 mr-2 flex-none disabled:opacity-0 disabled:cursor-default"
                                aria-label={intl.formatMessage({ id: "b0006" })}
                                onClick={() => updateSuggestions(toSpliced(sortableSuggestions, index, 1))}
                        >
                            <DeleteIcon className="text-point-red size-4" />
                        </Button>
                    </div>
                )}</SortableItemWrapper>
            ))}</SortableWrapper>
        </div>
    </>);
};


export default ParagraphOutputLLMAnswerCardInputs;
